import React, { useState, useEffect, useContext, useCallback } from "react";
import { CurrentUserContext, SocketContext } from "../../App";
import ChatListComponent from "../../components/chatComponents/ChatListComponent.jsx";
import ChatWindowComponent from "../../components/chatComponents/ChatWindowComponent.jsx";
import NewChatModal from "../../components/chatComponents/NewChatModal.jsx";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

export default function ChatPage() {
  const { currentUser } = useContext(CurrentUserContext);
  const { socket: globalSocket, socketRef } = useContext(SocketContext);
  const socket = globalSocket || socketRef?.current;
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);

  const token = currentUser?.token;
  const currentUserId = currentUser?.user_id;

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/getConversations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.message === "success") {
        setConversations(data.conversations || []);
      }
    } catch (err) {
      console.error("Error fetching conversations:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Refresh conversations when socket receives a new message
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = () => {
      fetchConversations();
    };

    socket.on("newMessage", handleNewMessage);
    return () => {
      socket.off("newMessage", handleNewMessage);
    };
  }, [socket, fetchConversations]);

  const handleSelectConversation = (convo) => {
    setSelectedConversation(convo);
    setMobileShowChat(true);
  };

  const handleBack = () => {
    setMobileShowChat(false);
    fetchConversations(); // refresh unread counts
  };

  const handleNewConversationCreated = async (conversationId) => {
    setShowNewChatModal(false);
    await fetchConversations();
    setConversations((prev) => {
      const found = prev.find((c) => c.conversation_id === conversationId);
      if (found) {
        setSelectedConversation(found);
        setMobileShowChat(true);
      }
      return prev;
    });
    try {
      const res = await fetch(`${API_URL}/getConversations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.message === "success") {
        const convos = data.conversations || [];
        setConversations(convos);
        const found = convos.find((c) => c.conversation_id === conversationId);
        if (found) {
          setSelectedConversation(found);
          setMobileShowChat(true);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div
      className="d-flex h-100 chat-page-container"
      style={{
        backgroundColor: "#1a1a2e",
        borderRadius: "16px",
        overflow: "hidden",
        height: "100%",
        maxHeight: "100%",
      }}
    >
      {/* Left Panel - Conversation List */}
      <div
        className={`chat-list-panel h-100 ${mobileShowChat ? "d-none d-md-flex" : "d-flex"}`}
        style={{ minWidth: 0 }}
      >
        <ChatListComponent
          conversations={conversations}
          selectedConversationId={selectedConversation?.conversation_id}
          onSelectConversation={handleSelectConversation}
          onNewChat={() => setShowNewChatModal(true)}
          currentUserId={currentUserId}
        />
      </div>

      {/* Right Panel - Chat Window */}
      <div
        className={`flex-grow-1 h-100 ${!mobileShowChat ? "d-none d-md-flex" : "d-flex"}`}
        style={{ flexDirection: "column", minWidth: 0 }}
      >
        <ChatWindowComponent
          conversation={selectedConversation}
          currentUserId={currentUserId}
          token={token}
          socket={socket}
          onBack={handleBack}
        />
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <NewChatModal
          token={token}
          currentUserId={currentUserId}
          onClose={() => setShowNewChatModal(false)}
          onConversationCreated={handleNewConversationCreated}
        />
      )}

      {/* Chat layout + typing animation styles */}
      <style>{`
        @keyframes typingDot {
          0%, 20% { opacity: 0; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        /* Mobile: chat list takes full width */
        .chat-list-panel {
          flex: 1 1 100%;
          width: 100%;
        }
        /* Desktop (md+): constrain chat list to sidebar width */
        @media (min-width: 768px) {
          .chat-list-panel {
            flex: 0 0 380px;
            max-width: 380px;
          }
        }
        /* Mobile: chat fills available space without overlap */
        @media (max-width: 768px) {
          .chat-page-container {
            border-radius: 0 !important;
            margin: -0.5rem -1rem 0 !important;
            width: calc(100% + 2rem) !important;
            height: calc(100vh - 56px - 72px - env(safe-area-inset-top) - env(safe-area-inset-bottom)) !important;
            max-height: calc(100vh - 56px - 72px - env(safe-area-inset-top) - env(safe-area-inset-bottom)) !important;
          }
        }
      `}</style>
    </div>
  );
}
